import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Badge } from "../../components/ui/Badge";

interface DocRow {
  id: number;
  nombre: string;
  tipo: string;
  esComun?: boolean;
  createdAt?: string;
}

const TIPO_LABELS: Record<string, string> = {
  poliza: "Póliza",
  parte_accidentes: "Parte accidentes",
  poliza_generica: "Póliza genérica",
  manual: "Manual",
  otro: "Otro",
};

export function DocumentosClientePage() {
  const [list, setList] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"mis" | "comunes">("mis");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDescargar = async (id: number, nombre: string) => {
    try {
      setDownloadError(null);
      const response = await api.get(`/cliente/documentos/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nombre}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status !== 401) {
        console.error("Error al descargar documento:", err);
        setDownloadError("No se pudo descargar el documento. Inténtalo de nuevo.");
      }
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<DocRow[]>("/cliente/documentos");
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const misDocs = list.filter((d) => !d.esComun);
  const comunes = list.filter((d) => d.esComun);
  const displayList = tab === "mis" ? misDocs : comunes;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Documentos</h1>

      <div className="flex max-w-full gap-2 overflow-x-auto border-b border-gray-200">
        <button type="button" onClick={() => setTab("mis")} className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium ${tab === "mis" ? "border-b-2 border-[#1A3AB8] text-[#1A3AB8]" : "text-gray-600"}`}>Mis documentos</button>
        <button type="button" onClick={() => setTab("comunes")} className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium ${tab === "comunes" ? "border-b-2 border-[#1A3AB8] text-[#1A3AB8]" : "text-gray-600"}`}>Documentos comunes</button>
      </div>

      {downloadError && <p className="text-sm text-red-600">{downloadError}</p>}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {displayList.length === 0 ? (
            <li className="py-8 text-center text-gray-500">No hay documentos</li>
          ) : (
            displayList.map((d) => (
              <li key={d.id} className="py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-[#2C2C2C]">{d.nombre}</p>
                  <Badge variant="neutral">{TIPO_LABELS[d.tipo] ?? d.tipo}</Badge>
                  {d.createdAt && <span className="text-xs text-gray-500 ml-2">{new Date(d.createdAt).toLocaleDateString()}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => handleDescargar(d.id, d.nombre)}
                  className="text-[#1A3AB8] hover:underline text-sm font-medium"
                >
                  Descargar
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
